import { useInfiniteQuery, useQuery } from "react-query";

import {
  getAllEmails,
  getEmailsStatistics,
  getMonthlyBoardEmailReports,
} from "services/clientCommunication";

import { reportFilterColumns } from 'modules/ClientCommunication/constants/constants';
import { servicesNames } from "constants/servicesNames.constants";

import { getPageFromUrl } from "helpers/PageFromUrl";

export const useInboxDataQuery = (
  selectedFsmsIds:any,
  fsmsData:any,
  activeTab:any,
  isSelectOpen:any,
  ownerName: string,
) => {
  const {
    data: inboxData,
    refetch: refetchInboxData,
    fetchNextPage: inboxFetchNextPage,
    hasNextPage: inboxHasNextPage,
    isFetching: inboxIsFetching,
    isFetchingNextPage: inboxIsFetchingNextPage,
  } = useInfiniteQuery(
    [servicesNames.getInboxEmailReportsData, { activeTab, ownerName }],
    ({ pageParam = 1 }) => getMonthlyBoardEmailReports({
      page: pageParam,
      column: reportFilterColumns.INBOX,
      fsm_ids: selectedFsmsIds,
      fsmsData,
      ownerName,
    }),
    {
      getNextPageParam: ({ data: { next = "" } = {} }) => {
        if (next) {
          const res = getPageFromUrl(next);
          return res ? Number(res) : undefined;
        }
      },
      enabled:
        activeTab === "monthlyBoard" &&
        selectedFsmsIds.length > 0 &&
        !isSelectOpen,
    },
  );

  return {
    inboxData,
    refetchInboxData,
    inboxFetchNextPage,
    inboxHasNextPage,
    inboxIsFetching,
    inboxIsFetchingNextPage,
  };
};

export const usePerformanceUpdateDoneDataQuery = (
  selectedFsmsIds:any,
  fsmsData:any,
  activeTab:any,
  isSelectOpen:any,
  ownerName: string,
) => {
  const {
    data: performanceUpdateDoneData,
    fetchNextPage: performanceUpdateDoneFetchNextPage,
    hasNextPage: performanceUpdateDoneHasNextPage,
    refetch: refetchPerformanceUpdateDoneData,
    isFetching: performanceUpdateDoneIsFetching,
    isFetchingNextPage: performanceUpdateDoneIsFetchingNextPage,
  } = useInfiniteQuery(
    [servicesNames.getPerformanceUpdateDoneEmailReportsData, { activeTab, ownerName }],
    ({ pageParam = 1 }) => getMonthlyBoardEmailReports({
      page: pageParam,
      column: reportFilterColumns.PERFORMANCE_UPDATE_DONE,
      fsm_ids: selectedFsmsIds,
      fsmsData,
      ownerName,
    }),
    {
      getNextPageParam: ({ data: { next = "" } = {} }) => {
        if (next) {
          const res = getPageFromUrl(next);
          return res ? Number(res) : undefined;
        }
      },
      enabled:
        activeTab === "monthlyBoard" &&
        selectedFsmsIds.length > 0 &&
        !isSelectOpen,
    },
  );

  return {
    performanceUpdateDoneData,
    performanceUpdateDoneFetchNextPage,
    performanceUpdateDoneHasNextPage,
    refetchPerformanceUpdateDoneData,
    performanceUpdateDoneIsFetching,
    performanceUpdateDoneIsFetchingNextPage,
  };
};

export const useDoneDataQuery = (
  selectedFsmsIds:any,
  fsmsData:any,
  activeTab:any,
  isSelectOpen:any,
  ownerName: string,
) => {
  const {
    data: doneData,
    fetchNextPage: doneFetchNextPage,
    hasNextPage: doneHasNextPage,
    refetch: refetchDoneData,
    isFetching: doneIsFetching,
    isFetchingNextPage: doneIsFetchingNextPage,
  } = useInfiniteQuery(
    [servicesNames.getDoneEmailReportsData, { activeTab, ownerName }],
    ({ pageParam = 1 }) => getMonthlyBoardEmailReports({
      page: pageParam,
      column: reportFilterColumns.DONE,
      fsm_ids: selectedFsmsIds,
      fsmsData,
      ownerName,
    }),
    {
      getNextPageParam: ({ data: { next = "" } = {} }) => {
        if (next) {
          const res = getPageFromUrl(next);
          return res ? Number(res) : undefined;
        }
      },
      enabled:
        activeTab === "monthlyBoard" &&
        selectedFsmsIds.length > 0 &&
        !isSelectOpen,
    },
  );

  return {
    doneData,
    doneFetchNextPage,
    doneHasNextPage,
    refetchDoneData,
    doneIsFetching,
    doneIsFetchingNextPage,
  };
};

export const useAllEmails = (
  activeTab:any,
  selectedTimePeriod:any,
  debouncedSearchValue:any,
  selectedStatuses:any,
  selectedTypes: string[],
  selectedFsmsIds:any,
  fsmsData:any,
) => {
  const {
    data: allEmailsResponse,
    refetch: refetchAllEmails,
    fetchNextPage: fetchNextAllEmailsPage,
    isFetchingNextPage: allEmailsIsFetchingNextPage,
    isFetching: allEmailsIsFetching,
  } = useInfiniteQuery(
    [
      servicesNames.getAllEmails,
      {
        activeTab,
        selectedTimePeriod,
        debouncedSearchValue,
      },
    ],
    ({ pageParam = 1 }) => getAllEmails({
      page: pageParam,
      status: selectedStatuses,
      type: selectedTypes,
      fsm_ids: selectedFsmsIds,
      period: selectedTimePeriod,
      fsmsData,
      ownerName: debouncedSearchValue,
    }),
    {
      onSuccess: (data) => {
        if (!data?.pages[0].count) {
          // setIsEmptyStateAllEmails(true);
        } else {
          if (selectedFsmsIds.length) {
            // setIsEmptyStateAllEmails(false);
          }
        }
      },
      getNextPageParam: (data) => {
        if (data?.next) {
          const res = getPageFromUrl(data?.next);
          return res ? Number(res) : undefined;
        }
      },
      enabled: activeTab === "allEmails",
    },
  );

  const allEmailsData:any = allEmailsResponse?.pages.map(page => page?.results?.map((item:any) => item));

  const flattenedArrayAllEmailsResponse = [].concat.apply([], allEmailsData);

  return {
    allEmailsResponse,
    refetchAllEmails,
    fetchNextAllEmailsPage,
    allEmailsIsFetchingNextPage,
    allEmailsIsFetching,
    flattenedArrayAllEmailsResponse,
  };
};

export const useStatistics = (activeTab:any, roleFromToken:any, selectedTimePeriod:any) => {
  const { data: { data: statisticsData } = {} } = useQuery(
    [
      servicesNames.getEmailsStatistics,
      { activeTab, roleFromToken, selectedTimePeriod },
    ],
    () => getEmailsStatistics({ period: selectedTimePeriod }),
    {
      enabled: activeTab === "allEmails" && roleFromToken === "admin",
    },
  );

  return { statisticsData };
};
